"use client";

/**
 * The single-recording flow shell (client-only pivot).
 * Home (hero + dropzone) → staged screen (optional context, key gate when
 * none is connected, explicit start — a dropped file NEVER auto-starts) →
 * in-browser transcription (with an interrupted state) → transcript +
 * notes workspace. One recording at a time; everything lives in tab memory.
 */
import { useRef, useState } from "react";
import { useTranslations } from "next-intl";
import { AlertTriangle, Lock, Music, X } from "lucide-react";
import { AudioDropzone } from "@/components/flow/audio-dropzone";
import { Workspace } from "@/components/flow/workspace";
import { OpenAIKeyForm } from "@/components/settings/openai-key-form";
import { useHasOpenAIKey } from "@/lib/client/openai-key";
import {
  SAMPLE_FILE_NAME,
  SAMPLE_SEGMENTS,
} from "@/lib/client/sample-transcript";
import {
  completeTranscription,
  createSessionAbort,
  failTranscription,
  loadSample,
  resetSession,
  setTranscriptionProgress,
  startSession,
  useSessionState,
  type TranscriptionProgress,
} from "@/lib/client/session-store";
import { TranscriptionError, transcribeAudio } from "@/lib/client/transcribe";
import { useUnloadWarning } from "@/hooks/use-unload-warning";
import { toast } from "@/lib/toast";
import { logError } from "@/lib/logging";

export function HomeFlow() {
  const t = useTranslations("Flow");
  const keyPresent = useHasOpenAIKey();
  const session = useSessionState();
  const [stagedFile, setStagedFile] = useState<File | null>(null);
  // Mirror of stagedFile for async continuations: the key form's onSaved
  // fires after a validation round-trip, so its onStart closure can be
  // stale — it must no-op unless THIS exact File is still the staged one.
  const stagedFileRef = useRef<File | null>(null);
  // Optional steering context (names, jargon) → Whisper's prompt field.
  // Collected on the staged screen; tab memory only, like everything else.
  const [transcriptionContext, setTranscriptionContext] = useState("");
  // Same stale-closure hazard as stagedFileRef: a start fired from the key
  // form's async onSaved runs a render-old transcribe(), which must still
  // read the context as typed by the time transcription actually starts.
  const transcriptionContextRef = useRef("");

  function stageFile(file: File | null) {
    stagedFileRef.current = file;
    setStagedFile(file);
  }

  function updateTranscriptionContext(value: string) {
    transcriptionContextRef.current = value;
    setTranscriptionContext(value);
  }

  useUnloadWarning(session.hasUnexportedContent);

  async function transcribe(file: File) {
    // The store owns the controller so every discard path (this screen's
    // cancel, the workspace's start-over, the header's go-home) aborts it.
    const controller = createSessionAbort();
    // The store keeps the context so the workspace can prefill the notes
    // focus field with it once transcription lands. Read through the ref —
    // this closure can be render-old (see transcriptionContextRef).
    startSession(file, transcriptionContextRef.current.trim());
    try {
      const segments = await transcribeAudio(file, {
        signal: controller.signal,
        prompt: transcriptionContextRef.current.trim() || undefined,
        onProgress: (doneChunks, totalChunks) => {
          if (!controller.signal.aborted) {
            setTranscriptionProgress({ doneChunks, totalChunks });
          }
        },
      });
      if (controller.signal.aborted) return;
      completeTranscription(segments);
    } catch (error) {
      if (
        controller.signal.aborted ||
        (error instanceof TranscriptionError && error.kind === "aborted")
      ) {
        return; // whoever aborted already reset or replaced the session
      }
      logError(error, "[flow] Transcription failed");
      failTranscription(
        error instanceof TranscriptionError
          ? t(`errors.${error.kind}`)
          : t("errors.generic"),
      );
    }
  }

  function handleCancel() {
    resetSession(); // aborts the in-flight pipeline before clearing state
  }

  if (session.flow === "transcribing") {
    return (
      <TranscribingScreen
        fileName={session.audioFile?.name ?? ""}
        progress={session.progress}
        error={session.error}
        onCancel={handleCancel}
        onRetry={() => {
          if (session.audioFile) void transcribe(session.audioFile);
        }}
        onDiscard={resetSession}
      />
    );
  }

  if (session.flow === "working") {
    return <Workspace />;
  }

  if (stagedFile) {
    return (
      <StagedScreen
        file={stagedFile}
        keyPresent={keyPresent}
        context={transcriptionContext}
        onContextChange={updateTranscriptionContext}
        onUnstage={() => stageFile(null)}
        onStart={() => {
          // Guard against a stale start: the user may have unstaged or
          // replaced the file while the key was still being validated.
          if (stagedFileRef.current !== stagedFile) return;
          stageFile(null);
          void transcribe(stagedFile);
        }}
      />
    );
  }

  return (
    <main className="flex flex-1 flex-col items-center justify-center px-7 pb-10 pt-14 text-center">
      <p className="mb-[18px] animate-[wd-rise_.5s_ease_both] font-mono text-[11.5px] font-medium uppercase tracking-[2.2px] text-primary">
        {t("landing.eyebrow")}
      </p>
      <h1 className="mb-3.5 max-w-[640px] animate-[wd-rise_.5s_ease_both] font-serif text-[clamp(38px,5.4vw,58px)] font-medium leading-[1.06] tracking-[-1.2px] text-balance [animation-delay:.06s]">
        {t.rich("landing.title", {
          em: (chunks) => <em className="italic text-primary">{chunks}</em>,
        })}
      </h1>
      <p className="mb-9 max-w-[520px] animate-[wd-rise_.5s_ease_both] text-[16.5px] leading-relaxed text-muted-foreground text-pretty [animation-delay:.12s]">
        {t("landing.subtitle")}
      </p>

      <div className="w-full max-w-[620px] animate-[wd-rise_.5s_ease_both] [animation-delay:.18s]">
        {/* Selecting a file only stages it — transcription starts from the
            staged screen, after the user had a chance to add context. */}
        <AudioDropzone onFileSelected={stageFile} />
      </div>

      <p className="mt-[18px] animate-[wd-rise_.5s_ease_both] text-[13.5px] text-muted-foreground [animation-delay:.24s]">
        {t("landing.samplePrompt")}{" "}
        <button
          type="button"
          onClick={() => {
            loadSample(
              new File([], SAMPLE_FILE_NAME, { type: "audio/mp4" }),
              SAMPLE_SEGMENTS,
            );
            toast.success(t("landing.sampleLoaded"));
          }}
          className="cursor-pointer font-medium text-primary underline underline-offset-[3px]"
        >
          {t("landing.sampleLink")}
        </button>
      </p>

      <div className="mt-[52px] grid w-full max-w-[620px] animate-[wd-rise_.5s_ease_both] grid-cols-[repeat(auto-fit,minmax(170px,1fr))] gap-[26px] text-left [animation-delay:.3s]">
        {([1, 2, 3] as const).map((step) => (
          <div key={step} className="flex flex-col gap-[5px]">
            <span className="font-mono text-[11px] font-semibold text-primary">
              0{step}
            </span>
            <span className="text-[13.5px] font-semibold">
              {t(`landing.step${step}Title`)}
            </span>
            <span className="text-[12.5px] leading-normal text-muted-foreground">
              {t(`landing.step${step}Description`)}
            </span>
          </div>
        ))}
      </div>

      <p className="mt-11 flex animate-[wd-rise_.5s_ease_both] items-center gap-[7px] text-xs text-muted-foreground [animation-delay:.36s]">
        <Lock className="h-3 w-3 flex-none" />
        {t("landing.ephemeralNote")}
      </p>
    </main>
  );
}

/**
 * Staged file before transcription: file chip + optional context field +
 * an explicit start. With no key connected the start is the key gate
 * ("connect & transcribe"); with one it's a plain start button.
 */
function StagedScreen({
  file,
  keyPresent,
  context,
  onContextChange,
  onUnstage,
  onStart,
}: {
  file: File;
  keyPresent: boolean;
  context: string;
  onContextChange: (value: string) => void;
  onUnstage: () => void;
  onStart: () => void;
}) {
  const t = useTranslations("Flow");
  const sizeLabel =
    file.size >= 1024 * 1024
      ? `${(file.size / (1024 * 1024)).toFixed(1)} MB`
      : `${Math.max(1, Math.round(file.size / 1024))} KB`;

  return (
    <main className="flex flex-1 flex-col items-center justify-center gap-3.5 px-7 py-12">
      <div className="flex w-full max-w-[460px] animate-[wd-rise_.4s_ease_both] items-center gap-3.5 rounded-xl border bg-card p-[14px_16px] shadow-elev">
        <div className="flex h-10 w-10 flex-none items-center justify-center rounded-[10px] bg-tint text-primary">
          <Music className="h-[17px] w-[17px]" />
        </div>
        <div className="min-w-0 flex-1 text-left">
          <p className="truncate text-sm font-semibold">{file.name}</p>
          <p className="mt-0.5 font-mono text-[11.5px] text-muted-foreground">
            {sizeLabel}
          </p>
        </div>
        <button
          type="button"
          onClick={onUnstage}
          aria-label={t("staged.removeFile")}
          className="flex h-7 w-7 flex-none items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>

      <div className="w-full max-w-[460px] animate-[wd-rise_.4s_ease_both] rounded-2xl border bg-card p-7 text-left shadow-elev [animation-delay:.08s]">
        <h2 className="mb-1.5 font-serif text-[26px] font-medium tracking-[-0.4px]">
          {keyPresent ? t("staged.readyTitle") : t("staged.title")}
        </h2>
        <p className="mb-5 text-[13.5px] leading-relaxed text-muted-foreground">
          {keyPresent ? t("staged.readyDescription") : t("staged.description")}
        </p>
        <div className="mb-5">
          <label
            htmlFor="transcription-context"
            className="mb-1.5 block text-[12.5px] font-semibold"
          >
            {t("context.label")}
          </label>
          <input
            id="transcription-context"
            type="text"
            value={context}
            onChange={(e) => onContextChange(e.target.value)}
            placeholder={t("context.placeholder")}
            autoFocus={keyPresent}
            className="h-10 w-full rounded-[9px] border border-input bg-background px-3 font-mono text-[13px] text-foreground outline-none transition-colors placeholder:text-muted-foreground focus:border-primary"
          />
          <p className="mt-1.5 text-xs text-muted-foreground">
            {t("context.hint")}
          </p>
        </div>
        {keyPresent ? (
          <button
            type="button"
            onClick={onStart}
            className="h-11 w-full rounded-[10px] bg-primary text-sm font-semibold text-primary-foreground transition-[filter] hover:brightness-105"
          >
            {t("staged.start")}
          </button>
        ) : (
          <>
            <OpenAIKeyForm
              submitLabel={t("staged.connect")}
              autoFocus
              onSaved={onStart}
              secondaryAction={
                <button
                  type="button"
                  onClick={onUnstage}
                  className="h-11 whitespace-nowrap rounded-[10px] border border-input px-[18px] text-sm font-medium transition-colors hover:bg-accent"
                >
                  {t("staged.notNow")}
                </button>
              }
            />
            <p className="mt-4 text-[11.5px] text-muted-foreground">
              {t("staged.whereKey")}
            </p>
          </>
        )}
      </div>
    </main>
  );
}

const PROGRESS_BAR_COUNT = 7;

function TranscribingScreen({
  fileName,
  progress,
  error,
  onCancel,
  onRetry,
  onDiscard,
}: {
  fileName: string;
  progress: TranscriptionProgress | null;
  error: string | null;
  onCancel: () => void;
  onRetry: () => void;
  onDiscard: () => void;
}) {
  const t = useTranslations("Flow.progress");
  // 4% floor so the bar reads as "started" while chunking is still planned.
  const pct = progress
    ? Math.round((progress.doneChunks / progress.totalChunks) * 100)
    : 4;

  return (
    <main className="flex flex-1 flex-col items-center justify-center px-7 py-12">
      <div className="w-full max-w-[480px] animate-[wd-rise_.4s_ease_both] rounded-2xl border bg-card p-[36px_32px] text-center shadow-elev">
        {error === null ? (
          <>
            <div className="mb-[22px] flex h-10 items-center justify-center gap-1">
              {Array.from({ length: PROGRESS_BAR_COUNT }, (_, i) => (
                <span
                  key={i}
                  className="h-[34px] w-[5px] origin-center animate-[wd-bar_1.1s_ease-in-out_infinite] rounded-[3px] bg-primary"
                  style={{ animationDelay: `${-i * 0.15}s` }}
                />
              ))}
            </div>
            <p className="mb-1 font-serif text-[23px] font-medium tracking-[-0.3px]">
              {progress ? t("transcribing") : t("preparing")}
            </p>
            <p className="mb-6 font-mono text-xs text-muted-foreground">
              {fileName}
            </p>
            <div className="mb-2.5 h-[5px] overflow-hidden rounded-[3px] bg-muted">
              <div
                className="h-full rounded-[3px] bg-primary transition-[width] duration-500"
                style={{ width: `${pct}%` }}
              />
            </div>
            <p className="mb-[26px] font-mono text-[11.5px] text-muted-foreground">
              {progress
                ? t("part", {
                    current: Math.min(
                      progress.doneChunks + 1,
                      progress.totalChunks,
                    ),
                    total: progress.totalChunks,
                  })
                : t("splitting")}
            </p>
            <p className="mb-5 text-[12.5px] text-muted-foreground">
              {t("keepOpen")}
            </p>
            <button
              type="button"
              onClick={onCancel}
              className="h-[38px] rounded-[9px] border border-input px-[18px] text-[13px] font-medium transition-colors hover:bg-accent"
            >
              {t("cancel")}
            </button>
          </>
        ) : (
          <>
            <div className="mx-auto mb-[18px] flex h-[46px] w-[46px] items-center justify-center rounded-full bg-tint text-primary">
              <AlertTriangle className="h-5 w-5" />
            </div>
            <p className="mb-1 font-serif text-[23px] font-medium tracking-[-0.3px]">
              {t("interruptedTitle")}
            </p>
            <p className="mb-6 font-mono text-xs text-muted-foreground">
              {fileName}
            </p>
            <div className="mb-3.5 h-[5px] overflow-hidden rounded-[3px] bg-muted">
              <div
                className="h-full rounded-[3px] bg-primary opacity-45"
                style={{ width: `${pct}%` }}
              />
            </div>
            <p
              role="alert"
              className="mb-1 text-[13px] leading-relaxed text-pretty"
            >
              {error}
            </p>
            <p className="mb-6 text-[13px] leading-relaxed text-muted-foreground text-pretty">
              {t("interruptedNote")}
            </p>
            <div className="flex flex-wrap justify-center gap-2.5">
              <button
                type="button"
                onClick={onRetry}
                className="h-10 whitespace-nowrap rounded-[10px] bg-primary px-[18px] text-[13.5px] font-semibold text-primary-foreground transition-[filter] hover:brightness-105"
              >
                {t("tryAgain")}
              </button>
              <button
                type="button"
                onClick={onDiscard}
                className="h-10 whitespace-nowrap rounded-[10px] border border-input px-[18px] text-[13.5px] font-medium transition-colors hover:bg-accent"
              >
                {t("discard")}
              </button>
            </div>
          </>
        )}
      </div>
    </main>
  );
}
